'use client';

import { usePathname } from 'next/navigation';
import { AppHeader } from '@/components/AppHeader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SportProvider } from '@/contexts/SportContext';
import { PricedSurfaceVersionPoller } from '@/components/PricedSurfaceVersionPoller';
import { SettingsVersionPoller } from '@/components/SettingsVersionPoller';
import { PastDueBanner } from '@/components/client/PastDueBanner';
import { AdminBanner } from '@/components/client/AdminBanner';
import { PostHogPersonProperties } from '@/components/posthog/PostHogPersonProperties';
import { WelcomeTourPromptGate } from '@/components/client/WelcomeTourPromptGate';
import type { Sport } from '@sharpr/types';

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const sport: Sport = pathname.startsWith('/mlb') ? 'mlb' : 'nba';

  // Sport navigations are derivable from $pageview (auto-captured via
  // capture_pageview: 'history_change'); use PostHog's Paths insight on $pageview
  // filtered to /nba and /mlb rather than a dedicated event.

  return (
    <SportProvider sport={sport}>
      {/* Writes subscription_status / sports_with_access / is_admin / trial_used as
          person properties — the backbone for cohort filters AND for deriving the
          "non-sub viewed a sport page" funnel from $pageview (so no paywall_hit event:
          there's no paywall in this layout anymore, odds are public + EV-teased). */}
      <PostHogPersonProperties />
      <SettingsVersionPoller />
      {/* NBA boards stay current over board:nba (reseeds, deltas); only MLB polls its version. */}
      {sport === 'mlb' && <PricedSurfaceVersionPoller sport={sport} />}
      <WelcomeTourPromptGate />
      <div className="h-screen flex flex-col bg-muted/20 dark:bg-bg-primary overflow-hidden">
        <PastDueBanner />
        <AppHeader />
        <AdminBanner />
        <div className="flex-1 overflow-hidden">
          {/* Odds pages are public now — anonymous + no-sub render the table with
              projections/EV stripped + a sign-up teaser (plan §2/§5). No Paywall
              gate here; access tiering happens in PlayerSelectionView. */}
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </div>
    </SportProvider>
  );
}
